
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import { Link, useLocation, useNavigate } from 'react-router';
import { SITE_NAME, SITE_URL } from '../constants';

type BlogPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  publishedAt: string | null;
};

type BlogResponse = {
  items: BlogPost[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

const getPageFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const rawPage = Number.parseInt(params.get('page') || '1', 10);
  return Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
};

const BlogPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialPage = getPageFromSearch(location.search);
  const bootstrap =
    typeof window !== 'undefined'
      ? ((window as typeof window & { __BLOG_BOOTSTRAP__?: BlogResponse }).__BLOG_BOOTSTRAP__ as BlogResponse | undefined)
      : ((globalThis as typeof globalThis & { __SSR_BLOG_BOOTSTRAP__?: BlogResponse }).__SSR_BLOG_BOOTSTRAP__ as BlogResponse | undefined);
  const bootstrapMatchesPage = bootstrap?.page === initialPage;
  const bootstrapRef = useRef(bootstrapMatchesPage ? bootstrap : undefined);
  const [posts, setPosts] = useState<BlogPost[]>(bootstrapMatchesPage ? bootstrap.items : []);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!bootstrapMatchesPage);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(bootstrapMatchesPage ? bootstrap.totalPages : 1);

  useEffect(() => {
    const nextPage = getPageFromSearch(location.search);
    setPage((currentPage) => (currentPage === nextPage ? currentPage : nextPage));
  }, [location.search]);

  useEffect(() => {
    let isMounted = true;
    const loadPosts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (bootstrapRef.current && page === bootstrapRef.current.page) {
          if (typeof window !== 'undefined') {
            delete (window as any).__BLOG_BOOTSTRAP__;
          }
          setPosts(bootstrapRef.current.items);
          setTotalPages(bootstrapRef.current.totalPages);
          setIsLoading(false);
          bootstrapRef.current = undefined;
          return;
        }

        const response = await fetch(`/api/blogposts?page=${page}&limit=6`);
        if (!response.ok) {
          throw new Error('Request failed');
        }
        const payload = await response.json();
        if (isMounted) {
          setPosts(Array.isArray(payload?.items) ? payload.items : []);
          setTotalPages(Number.isFinite(payload?.totalPages) ? payload.totalPages : 1);
        }
      } catch (err) {
        if (isMounted) {
          setError('Unable to load posts right now.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    loadPosts();
    return () => {
      isMounted = false;
    };
  }, [page]);

  const formattedPosts = useMemo(
    () =>
      posts.map((post) => ({
        ...post,
        dateLabel: post.publishedAt
          ? new Date(post.publishedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })
          : 'Recent',
      })),
    [posts]
  );
  const canonical = page > 1 ? `${SITE_URL}/blog?page=${page}` : `${SITE_URL}/blog`;
  const schema = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: `Aviation Insights Blog | ${SITE_NAME}`,
      description: 'Stay updated with the latest trends in digital eAIP, aviation safety, and ICAO compliance.',
      url: canonical,
      publisher: {
        '@type': 'Organization',
        name: SITE_NAME,
        logo: `${SITE_URL}/AviaGov.svg`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: formattedPosts.map((post, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}/blog/${post.slug}`,
        name: post.title,
      })),
    },
  ]), [canonical, formattedPosts]);

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Aviation Insights Blog"
        description="Stay updated with the latest trends in digital eAIP, aviation safety, and ICAO compliance."
        canonical={canonical}
        ogType="article"
        schema={schema}
      />
      <section className="py-24 px-4 max-w-7xl mx-auto">
        <h1 className="text-5xl font-bold font-heading mb-4">Insights & News</h1>
        <p className="text-slate-500 text-xl mb-16">Latest updates from the frontlines of aviation technology.</p>

        {isLoading ? (
          <p className="text-slate-400">Loading posts...</p>
        ) : error ? (
          <p className="text-red-400">{error}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {formattedPosts.map((post, i) => (
              <GlowCard key={post.id || post.slug} delay={i * 100}>
                <div className="text-xs text-accent font-bold mb-4">{post.category || 'Insights'}</div>
                <h3 className="text-xl font-bold text-white mb-4 leading-tight">{post.title}</h3>
                <p className="text-slate-400 text-sm mb-8">{post.excerpt}</p>
                <div className="flex justify-between items-center mt-auto">
                  <span className="text-slate-500 text-xs">{post.dateLabel}</span>
                  <Link to={`/blog/${post.slug}`} className="text-white font-bold text-xs hover:underline">Read Post &rarr;</Link>
                </div>
              </GlowCard>
            ))}
          </div>
        )}
        {!isLoading && !error && totalPages > 1 ? (
          <div className="mt-12 flex items-center justify-center gap-4 text-sm">
            <button
              type="button"
              onClick={() => navigate(`/blog?page=${Math.max(1, page - 1)}`)}
              disabled={page <= 1}
              className="px-4 py-2 rounded-full border border-white/10 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-accent transition-colors"
            >
              Previous
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              type="button"
              onClick={() => navigate(`/blog?page=${Math.min(totalPages, page + 1)}`)}
              disabled={page >= totalPages}
              className="px-4 py-2 rounded-full border border-white/10 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-accent transition-colors"
            >
              Next
            </button>
          </div>
        ) : null}

        <div className="mt-32 p-12 bg-accent/5 border border-accent/20 rounded-[2rem] text-center max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold mb-4">Subscribe to the AIRAC Bulletin</h3>
          <p className="text-slate-400 mb-8">Get mission-critical regulatory summaries delivered to your inbox every 28 days.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <input type="email" placeholder="work@authority.gov" className="px-6 py-4 bg-primary border border-white/10 rounded-xl focus:outline-none focus:border-accent min-w-[300px]" />
            <button className="px-10 py-4 bg-accent text-primary font-bold rounded-xl">Join 5,000+ Pros</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
